import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { useOktaAuth } from '@okta/okta-react';

export const ProjectSelector = ({ selectedProject, onProjectChange }) => {
    const [projects, setProjects] = useState([]);
    const [projectsData, setProjectsData] = useState([]); // Store full project data including type
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const { authState } = useOktaAuth();

    const fetchProjects = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
                console.log('[ProjectSelector] Auth State:', {
                    isAuthenticated: authState?.isAuthenticated,
                    hasAccessToken: !!authState?.accessToken,
                });

                if (!authState?.accessToken) {
                    console.log('[ProjectSelector] No access token available');
                    setError('Authentication required - please log in');
                    setIsLoading(false);
                    return;
                }

                const token = typeof authState.accessToken === 'string' ? authState.accessToken : authState.accessToken?.accessToken;
                if (!token || typeof token !== 'string') {
                    console.error('[ProjectSelector] Token is not a string:', authState.accessToken);
                    setError('Invalid token format - please refresh and log in again');
                    setIsLoading(false);
                    return;
                }

                const response = await fetch('/api/projects', {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                
                console.log('[ProjectSelector] API Response:', response.status, response.statusText);
                
                if (!response.ok) {
                    if (response.status === 401) {
                        throw new Error('Authentication failed - please refresh and log in again');
                    }
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                
                if (data.success) {
                    // Store full project data
                    setProjectsData(data.projects || []);

                    const projectOptions = (data.projects || []).map(project => ({
                        value: project.key,
                        label: `${project.key} - ${project.name}`
                    }));
                    setProjects(projectOptions);
                } else {
                    setError(data.error || data.message || 'Failed to fetch projects');
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
    }, [authState]);

    useEffect(() => {
        if (authState?.isAuthenticated) {
            console.log('[ProjectSelector] User authenticated, fetching projects...');
            fetchProjects();
        } else if (authState?.isAuthenticated === false) {
            console.log('[ProjectSelector] User not authenticated');
            setIsLoading(false);
            setError('Please log in to access JIRA projects');
        } else {
            console.log('[ProjectSelector] Authentication state pending...');
        }
    }, [authState, fetchProjects]);

    // If the saved selectedProject isn't in the loaded list (renamed in JIRA,
    // permissions revoked, wrong tenant, stale localStorage, etc.) clear it
    // so downstream forms stop firing 404s against a key that doesn't exist.
    useEffect(() => {
        if (isLoading) return;
        if (!selectedProject) return;
        if (projects.length === 0) return;
        if (!projects.some(p => p.value === selectedProject)) {
            console.warn(`[ProjectSelector] Saved project "${selectedProject}" not in loaded list — clearing selection`);
            onProjectChange(null);
        }
    }, [isLoading, projects, selectedProject, onProjectChange]);

    const selectedOption = (projects || []).find(option => option.value === selectedProject) || null;

    const handleProjectChange = (selectedOption) => {
        onProjectChange(selectedOption ? selectedOption.value : null);
    };

    // Get the project type for the selected project
    const selectedProjectData = projectsData.find(p => p.key === selectedProject);
    const projectType = selectedProjectData?.projectTypeKey || null;
    
    // Map project type keys to display names
    const projectTypeLabels = {
        'software': 'Software',
        'business': 'Business',
        'service_desk': 'Service Desk',
        'product_discovery': 'Product Discovery',
        'unknown': 'Unknown'
    };
    
    const projectTypeDisplay = projectType ? projectTypeLabels[projectType] || projectType : null;

    return (
        <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span>Jira Project</span>
                {projectTypeDisplay && (
                    <span style={{ 
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        padding: '0.125rem 0.5rem',
                        borderRadius: '12px',
                        backgroundColor: '#E3FCEF',
                        color: '#006644',
                        textTransform: 'capitalize'
                    }}>
                        {projectTypeDisplay}
                    </span>
                )}
            </label>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ flex: 1 }}>
                    <Select
                        value={selectedOption}
                        onChange={handleProjectChange}
                        options={projects}
                        isLoading={isLoading}
                        isClearable
                        placeholder={
                            isLoading ? "Loading projects..." :
                            projects.length === 0 ? "No projects available — see message below" :
                            "Select a project"
                        }
                        styles={{
                            container: (base) => ({
                                ...base,
                                color: '#000',
                            }),
                            control: (base) => ({
                                ...base,
                                minHeight: 'calc(1.5em + 1.5rem + 2px)',
                            }),
                        }}
                    />
                </div>
            </div>

            {error && (
                <div style={{ color: '#cf1322', marginTop: '0.5rem' }}>
                    Couldn't load projects from JIRA: {error}
                </div>
            )}

            {/* Empty-but-no-error state: server replied OK but JIRA returned 0 projects.
                Most common cause is the API-token user not being a member of any project. */}
            {!isLoading && !error && projects.length === 0 && (
                <div style={{
                    marginTop: '0.5rem',
                    padding: '10px 12px',
                    backgroundColor: '#FFF7E6',
                    border: '1px solid #FFA940',
                    borderRadius: 6,
                    color: '#874D00',
                    fontSize: '0.85rem',
                    lineHeight: 1.5,
                }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>JIRA returned 0 projects.</div>
                    <div>Authentication succeeded, but the account behind your JIRA API token has no visible projects. Try one of these:</div>
                    <ul style={{ margin: '6px 0 0 20px', padding: 0 }}>
                        <li>Confirm the JIRA user (<code>JIRA_EMAIL</code> in <code>.env</code>) has <strong>Browse Projects</strong> permission on at least one project.</li>
                        <li>Confirm <code>JIRA_BASE_URL</code> points to the right Atlassian tenant — projects in a different tenant won't show.</li>
                        <li>If your token was rotated or revoked, regenerate it at <code>id.atlassian.com</code> → Security → API tokens, and update <code>.env</code>.</li>
                        <li>If you use OAuth for JIRA elsewhere, your prod server may see more projects than your local Basic-Auth fallback. The local fallback only sees what the API-token user can see.</li>
                    </ul>
                    <button
                        onClick={fetchProjects}
                        style={{
                            marginTop: 8,
                            padding: '4px 10px',
                            backgroundColor: '#FFF',
                            border: '1px solid #FFA940',
                            borderRadius: 4,
                            color: '#874D00',
                            fontSize: '0.8rem',
                            cursor: 'pointer',
                        }}
                    >
                        🔄 Retry
                    </button>
                </div>
            )}

            <div style={{ color: '#505F79', marginTop: '0.5rem', fontSize: '0.8rem' }}>
                Using JIRA API Token authentication
            </div>
        </div>
    );
};
